import React from "react";
import Select from "components/ui/Select";
import type { CoursePricingType } from "services/instructorCoursesService";
import { DEFAULT_COURSE_CURRENCY } from "services/instructorCoursesService";

export type CoursePricingFieldsProps = {
  isArabic: boolean;
  pricingType: CoursePricingType;
  price: string;
  onPricingTypeChange: (next: CoursePricingType) => void;
  onPriceChange: (next: string) => void;
  priceError?: string | null;
  className?: string;
};

/**
 * Shared pricing section for course create/edit forms.
 * Currency is fixed to EGP, so only type + price are shown.
 */
export default function CoursePricingFields({
  isArabic,
  pricingType,
  price,
  onPricingTypeChange,
  onPriceChange,
  priceError,
  className = "",
}: CoursePricingFieldsProps) {
  const isPaid = pricingType === "paid";

  return (
    <div
      className={`md:col-span-2 rounded-xl border border-gray-200 bg-gray-50/60 p-4 dark:border-white/10 dark:bg-white/5 ${className}`}
    >
      <h3 className="mb-3 text-sm font-bold text-navy-700 dark:text-white">
        {isArabic ? "تسعير الكورس" : "Course pricing"}
      </h3>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <p className="mb-1 text-xs font-semibold text-gray-600 dark:text-gray-300">
            {isArabic ? "نوع الكورس" : "Course type"}
          </p>
          <Select
            value={pricingType}
            onChange={(next) => onPricingTypeChange((next as CoursePricingType) || "free")}
            options={[
              { value: "free", label: isArabic ? "مجاني" : "Free" },
              { value: "paid", label: isArabic ? "مدفوع" : "Paid" },
            ]}
            isArabic={isArabic}
            allowEmpty={false}
            clearable={false}
          />
        </div>

        {isPaid ? (
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">
              {isArabic ? "سعر الكورس *" : "Course price *"}
            </label>
            <div
              className={`flex items-stretch overflow-hidden rounded-lg border bg-white focus-within:border-brand-400 dark:bg-navy-900 ${
                priceError
                  ? "border-red-500 focus-within:border-red-500"
                  : "border-gray-200 dark:border-white/10"
              }`}
            >
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                dir="ltr"
                value={price}
                onChange={(e) => onPriceChange(e.target.value)}
                placeholder={isArabic ? "مثلاً: 250" : "e.g. 250"}
                className="w-full bg-transparent px-3 py-2 text-sm text-start outline-none [appearance:textfield] dark:text-white [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <span className="flex shrink-0 items-center border-s border-gray-200 bg-gray-50 px-3 text-xs font-semibold text-gray-500 dark:border-white/10 dark:bg-white/5 dark:text-gray-400">
                {DEFAULT_COURSE_CURRENCY}
              </span>
            </div>
            {priceError ? (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{priceError}</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
